import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Logo from '../../Common/Logo';
import Icon from '../../Common/Icon';

const SECTION_LINKS = [
  { href: '#examples', label: 'Product', description: 'See the learning system', icon: 'box' },
  { href: '#loop', label: 'How it works', description: 'Follow the evidence-led loop', icon: 'repeat' },
  { href: '#privacy', label: 'For teams', description: 'Learning at work', icon: 'users' },
  { href: '#plans', label: 'Pricing', description: 'Compare plans', icon: 'credit-card' }
];

const SUPPORT_LINKS = [
  { label: 'Downloads', description: 'Private desktop app', icon: 'download', action: 'downloads' },
  { label: 'Resources', description: 'Guides and help', icon: 'book-open', action: 'resources' }
];

const LandingNavigation = React.memo(({
  onLoginClick,
  onSignupClick,
  onResourcesClick,
  onDownloadsClick,
  mobileMenuOpen,
  setMobileMenuOpen,
  darkMode,
  onThemeToggle,
}) => {
  const mobileToggleRef = useRef(null);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setMobileMenuOpen(false);
      mobileToggleRef.current?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  const handleResourcesClick = () => {
    closeMobileMenu();
    onResourcesClick();
  };

  const handleDownloadsClick = () => {
    closeMobileMenu();
    onDownloadsClick();
  };

  return (
    <header className="landing-header">
      <nav className="landing-nav" aria-label="Primary">
        <a href="#top" className="landing-brand" onClick={closeMobileMenu}>
          <Logo size={34} />
          <span className="landing-brand-name">ThryveLoop</span>
        </a>

        <div className="landing-nav-links">
          {SECTION_LINKS.map((link) => (
            <a key={link.href} href={link.href} className="landing-nav-link">
              {link.label}
            </a>
          ))}
          <button type="button" className="landing-nav-link landing-nav-link-button" onClick={handleDownloadsClick}>
            Downloads
          </button>
          <button type="button" className="landing-nav-link landing-nav-link-button" onClick={handleResourcesClick}>
            Resources
          </button>
        </div>

        <div className="landing-nav-actions">
          <button
            type="button"
            onClick={onThemeToggle}
            className="landing-theme-toggle"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Icon name={darkMode ? 'sun' : 'moon'} size={18} />
          </button>
          <button type="button" onClick={onLoginClick} className="landing-btn landing-btn--ghost">
            Log in
          </button>
          <button type="button" onClick={onSignupClick} className="landing-btn landing-btn--primary">
            Sign up
          </button>
        </div>

        <div className="landing-mobile-controls">
          <button
            type="button"
            onClick={onThemeToggle}
            className="landing-theme-toggle landing-theme-toggle--mobile"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Icon name={darkMode ? 'sun' : 'moon'} size={18} />
          </button>
          <button
            ref={mobileToggleRef}
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="landing-mobile-toggle"
            aria-expanded={mobileMenuOpen}
            aria-controls="landing-mobile-menu"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <Icon name="x" size={20} />
            ) : (
              <span className="landing-mobile-toggle-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            )}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="landing-mobile-drawer" id="landing-mobile-menu">
          <div className="landing-mobile-intro">
            <h2>Explore ThryveLoop</h2>
            <p>Choose where to begin.</p>
          </div>

          <nav className="landing-mobile-navigation" aria-label="Mobile navigation">
            <div className="landing-mobile-links landing-mobile-links--primary">
              {SECTION_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="landing-mobile-link"
                  onClick={closeMobileMenu}
                >
                  <span className="landing-mobile-link-icon">
                    <Icon name={link.icon} size={19} strokeWidth={1.8} />
                  </span>
                  <span className="landing-mobile-link-copy">
                    <span className="landing-mobile-link-title">{link.label}</span>
                    <span className="landing-mobile-link-description">{link.description}</span>
                  </span>
                </a>
              ))}
            </div>

            <div className="landing-mobile-links landing-mobile-links--support">
              {SUPPORT_LINKS.map((link) => (
                <button
                  key={link.action}
                  type="button"
                  className="landing-mobile-link landing-mobile-link-button"
                  onClick={link.action === 'downloads' ? handleDownloadsClick : handleResourcesClick}
                >
                  <span className="landing-mobile-link-icon">
                    <Icon name={link.icon} size={19} strokeWidth={1.8} />
                  </span>
                  <span className="landing-mobile-link-copy">
                    <span className="landing-mobile-link-title">{link.label}</span>
                    <span className="landing-mobile-link-description">{link.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </nav>

          <div className="landing-mobile-actions">
            <button
              type="button"
              className="landing-mobile-theme-action"
              onClick={onThemeToggle}
              role="switch"
              aria-checked={darkMode}
            >
              <span className="landing-mobile-link-icon">
                <Icon name="moon" size={19} strokeWidth={1.8} />
              </span>
              <span className="landing-mobile-link-copy">
                <span className="landing-mobile-link-title">Dark mode</span>
                <span className="landing-mobile-link-description">Appearance</span>
              </span>
              <span className="landing-mobile-theme-state">{darkMode ? 'On' : 'Off'}</span>
            </button>
            <div className="landing-mobile-auth-actions">
              <button
                type="button"
                className="landing-btn landing-btn--ghost"
                onClick={() => {
                  closeMobileMenu();
                  onLoginClick();
                }}
              >
                Log in
              </button>
              <button
                type="button"
                className="landing-btn landing-btn--primary"
                onClick={() => {
                  closeMobileMenu();
                  onSignupClick();
                }}
              >
                Sign up
                <Icon name="arrow-right" size={16} strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
});

LandingNavigation.propTypes = {
  onLoginClick: PropTypes.func.isRequired,
  onSignupClick: PropTypes.func.isRequired,
  onResourcesClick: PropTypes.func.isRequired,
  onDownloadsClick: PropTypes.func.isRequired,
  mobileMenuOpen: PropTypes.bool.isRequired,
  setMobileMenuOpen: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
  onThemeToggle: PropTypes.func.isRequired,
};

LandingNavigation.displayName = 'LandingNavigation';

export default LandingNavigation;
